/**
 * MeridianGuard - Route protection for Meridian-only features
 *
 * Restricts access to internal/admin routes to authenticated users with @meridian.ai email addresses.
 * Shows 404 page for both unauthenticated users and authenticated non-Meridian users.
 * This prevents revealing the existence of internal routes to external users.
 */

import { ReactNode } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useIsMeridianUser } from '../contexts/FeatureFlagContext';
import NotFoundPage from '../pages/NotFoundPage';
import { Logo } from './Logo';

interface MeridianGuardProps {
  children: ReactNode;
}

export function MeridianGuard({ children }: MeridianGuardProps) {
  const { user, loading: authLoading } = useAuth();
  const isMeridianUser = useIsMeridianUser();

  // Show loading state while checking authentication
  if (authLoading) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ background: '#F5F0EB' }}
      >
        <div className="mb-8">
          <Logo size={48} variant="default" />
        </div>
        <div className="flex items-center gap-3" style={{ color: '#6B6B6B' }}>
          <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
            <circle
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="60"
              strokeDashoffset="20"
              opacity="0.3"
            />
          </svg>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Show 404 for non-authenticated users or non-Meridian users
  // This prevents revealing the existence of internal routes
  if (!user || !isMeridianUser) {
    return <NotFoundPage />;
  }

  // User is authenticated and is a Meridian user
  return <>{children}</>;
}
